import React from 'react';
import Header from './components/Header';
import WelcomeOverlay from './components/WelcomeOverlay';
import { Link } from 'react-router-dom';
import { Container, Typography, Grid, Card, CardContent, CardActions, Button, Box } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useAuth } from './context/AuthContext';
import VersionFooter from './components/VersionFooter';

const menuItems = [
  {
    title: 'Your Personal Dashboard',
    description: 'Track your progress, review course scores, and access your completion certificates.',
    link: '/dashboard',
    icon: <DashboardIcon color="primary" sx={{ fontSize: 40 }} />,
    buttonText: 'Dashboard'
  },
  {
    title: 'Verify any Certificate',
    description: 'Validate the authenticity of any DeepAlert course certificate using its authenticity ID.',
    link: '/verify',
    icon: <VerifiedUserIcon color="primary" sx={{ fontSize: 40 }} />,
    buttonText: 'Verify Now'
  }
];

function Home() {
  const { user } = useAuth();
  const isSuperUser = (user?.email || '').toLowerCase() === 'johan@deepalert.ai';

  const allMenuItems = [...menuItems];

  // Add Profile card for editing first name and surname (email is read-only via Google)
  allMenuItems.push({
    title: 'Your Profile',
    description: 'Update your first name and surname for certificates. Email is managed by Google.',
    link: '/profile',
    icon: <AccountCircleIcon color="primary" sx={{ fontSize: 40 }} />,
    buttonText: 'Edit Profile'
  });

  if (isSuperUser) {
    allMenuItems.push({
      title: 'Back Office',
      description: 'Access administrative tools and system management features.',
      link: process.env.REACT_APP_ADMIN_URL || 'http://localhost:3001',
      icon: <AdminPanelSettingsIcon color="primary" sx={{ fontSize: 40 }} />,
      buttonText: 'Enter'
    });
  }
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0054A6 0%, #00386e 55%, #001f3f 100%)'
    }}>
      <Header />
      <WelcomeOverlay />
      <Container maxWidth="lg" sx={{ mt: 2, mb: 4, px: 0 }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 5 } }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{
            fontWeight: 'bold',
            color: 'white',
            textShadow: '0px 2px 4px rgba(0,0,0,0.6), 0px 1px 2px rgba(0,0,0,0.4)'
          }}>
            Training Academy
          </Typography>
          <Typography variant="h6" sx={{
            color: 'white',
            textShadow: '0px 1px 3px rgba(0,0,0,0.5), 0px 0px 1px rgba(0,0,0,0.3)'
          }}>
            Your centre of excellence
          </Typography>
        </Box>

        <Box sx={{ px: { xs: 2, md: 5 } }}>
          <Grid
            container
            rowSpacing={{ xs: 2, md: 3 }}
            columnSpacing={{ xs: 3, md: 5 }}
            justifyContent="center"
            alignItems="stretch"
          >
            {allMenuItems.map((item) => (
              <Grid item key={item.title} xs={12} sm={6} md={isSuperUser ? 3 : 4}>
                <Card sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: 'center',
                  p: 2,
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.28)'
                  }
                }}>
                  <Box sx={{ mb: 1, color: 'primary.main' }}>
                    {item.icon}
                  </Box>
                  <CardContent sx={{ flexGrow: 1, p: 1 }}>
                    <Typography variant="h5" component="h2" sx={{ fontWeight: 500, mb: 1 }}>
                      {item.title}
                    </Typography>
                    <Typography color="text.secondary">
                      {item.description}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'center', pt: 0 }}>
                    <Button
                      component={item.link.startsWith('http') ? 'a' : Link}
                      href={item.link.startsWith('http') ? item.link : null}
                      to={item.link.startsWith('http') ? null : item.link}
                      target={item.link.startsWith('http') ? '_blank' : null}
                      rel={item.link.startsWith('http') ? 'noopener noreferrer' : null}
                      variant="contained"
                      size="large"
                    >
                      {item.buttonText}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
      <VersionFooter />
    </Box>
  );
}

export default Home;
